import { Trans } from 'react-i18next';
import { type ComponentProps, type ReactElement, useMemo } from 'react';
import Link from 'next/link';
import { useTranslation } from 'next-i18next/pages';
import { useQueryClient } from '@tanstack/react-query';
import { mapSearchStateToAiSearchContext, mapSearchStateToFilterContext } from '@pkgs/components/utils/tracking';
import { getSortValues } from '@pkgs/components/utils/stringUtils';
import { type AdditionalUiStateParams } from '@pkgs/components/types/types';
import { PageType, TemplateType } from '@pkgs/components/types/tracking';
import { type SmartSortConfig } from '@pkgs/components/index';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { DEFAULT_BUSINESS_AREA } from '@pkgs/components/hooks/stores/search/slices/objectTypeFilterSlice';
import { type MapMode } from '@pkgs/components/hooks/stores/search/slices/mapInteractionSlice';
import { getShopsQueryKey } from '@pkgs/components/hooks/queries/queryKeys';
import { FeatureFlags } from '@pkgs/components/consts/consts';
import { SearchModule, type TFunction, type TransComponent } from '@pkgs/components';
import { Language, MarketCountryCode } from '@pkgs/api/models';
import { type MasterDataListResponseData, type SearchListingsFilters, type SearchListingsOptions } from '@pkgs/api';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import {
  type BusinessDivisionType,
  getAiSearchContext,
  getDivisionContext,
  getPageContext,
  getPropertyFilterContext,
  getShopContext,
  getSortContext,
} from '@ev-tech/snowplow-library';
import { useRegisterChatbotContext } from '@ev-tech/search-modules-chatbot';
import { useContactWizard } from '@ev-tech/lead-flows';
import { datadogRum } from '@datadog/browser-rum';

import { type PropertyTrackingIds } from '@/types/property.types';
import { useSearchPageProps, SearchPageProviders } from '@/providers/SearchPageProviders';
import { useDidomi } from '@/providers/DidomiContextProvider';
import { type AppPageProps } from '@/pages/_app';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useGTMTracking } from '@/hooks/usGTMTracking';
import { useWatchlist } from '@/hooks/useWatchlist';
import { useTrackPageView } from '@/hooks/useTrackPageView';
import { useSettings } from '@/hooks/useSettings';
import { useSearchApi } from '@/hooks/useSearchApi';
import { useGTMDataLayer } from '@/hooks/useGTMDataLayer';
import { useGAEvent, useGAEventOnce, useGTMTackingIdsEvents } from '@/hooks/useGAEvent';
import { useExposePreviousUrlStore } from '@/hooks/useExposePreviousUrlStore';
import { INTIAL_OPTIONS } from '@/consts/options';
import { metaDescriptionsIndex } from '@/consts/metaDescriptions';
import { DEFAULT_MAP_BOUNDING_BOX, DEFAULT_MAP_BOUNDING_BOX_AMERICAS, DEFAULT_MAP_BOUNDING_BOX_EUROPE } from '@/consts/map';
import { CONFIG } from '@/consts/config';
import { getMapABTestContext } from '@/components/ui-core-components-wrapper/lib/tracking/snowplow';
import { Layout } from '@/components/common/Layout/Layout';
import { ImageComponent } from '@/components/common/ImageComponent/ImageComponent';
import { Head } from '@/components/common/Head/Head';

import styles from '@/styles/propertySearchPage.module.css';

export type PropertySearchPageProps = AppPageProps & {
  initialFilters: SearchListingsFilters;
  initialOptions: SearchListingsOptions;
  hasLicencePartnerGAAccount: boolean;
  isLipaPortfolioPage: boolean;
  isMapToggledOnInitially: boolean;
  additionalUIStateParams?: AdditionalUiStateParams;
  trackingIds?: PropertyTrackingIds;
  mapMode?: MapMode;
  smartSortPreferences?: SmartSortConfig;
};

const SearchModuleTransComponent: TransComponent = ({ components, i18nKey, t, values }) => {
  return <Trans components={components as never} i18nKey={i18nKey as never} t={t as never} values={values} />;
};

const SearchResultLink = (props: ComponentProps<typeof Link>) => {
  const { setPreviousUrl } = useExposePreviousUrlStore();

  return (
    <Link
      {...props}
      onNavigate={(event) => {
        setPreviousUrl(window.location.href);
        props.onNavigate?.(event);
      }}
    />
  );
};

export const SearchPageContent = () => {
  const { currency, measurementSystem, countryCode, language } = useSettings();
  const { handleWatchlistClick, watchlist } = useWatchlist();
  const { t: tCommon } = useTranslation(TRANSLATION_NAMESPACE.common);
  const { t: tSearchModule } = useTranslation(TRANSLATION_NAMESPACE.search_module);
  const { t: tUserAccount } = useTranslation(TRANSLATION_NAMESPACE.user_account);
  const { isSnowplowInitialized } = useDidomi();
  const { open } = useContactWizard();
  const queryClient = useQueryClient();

  useRegisterChatbotContext({ pageType: PageType.SEARCH_RESULT });

  const {
    isLipaPortfolioPage,
    initialFilters,
    isMapToggledOnInitially,
    hasLicencePartnerGAAccount,
    spearHeadNavigation,
    pageLanguages,
    countryLinks,
    initialOptions,
    trackingIds,
    smartSortPreferences,
    additionalUIStateParams,
    featureFlags,
  } = useSearchPageProps();

  const searchApi = useSearchApi();
  const shopIds = initialFilters.masterDataShopIds ?? initialFilters.shopIds ?? [];
  const shops = queryClient.getQueryData<MasterDataListResponseData>(getShopsQueryKey({ shopIds }));
  const shop = Object.values(shops ?? {})[0];

  // TODO: Check if we can move this inside the SearchModule itself (https://engelvoelkers.atlassian.net/browse/NS-12540)
  const mapBoundingBoxFallback = useMemo(() => {
    if (countryCode !== MarketCountryCode['private-office']) return DEFAULT_MAP_BOUNDING_BOX;
    if (language === Language.en) return DEFAULT_MAP_BOUNDING_BOX_AMERICAS;
    return DEFAULT_MAP_BOUNDING_BOX_EUROPE;
  }, [countryCode, language]);

  const pageViewContexts = useMemo(
    () => [
      getPageContext({
        isHQPage: true,
        type: isLipaPortfolioPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT,
        page_language: language,
        country_code: countryCode,
        template_type: TemplateType.SEARCH_RESULT,
      }),
      getPropertyFilterContext(mapSearchStateToFilterContext(initialFilters, currency, measurementSystem)),
      getSortContext({
        ...getSortValues({
          sortingOption: initialFilters.sortingOptions?.[0],
          sortingOptionId: additionalUIStateParams?.sortingOptionId,
        }),
      }),
      getDivisionContext({
        name: (initialFilters.businessArea[0] ?? DEFAULT_BUSINESS_AREA) as BusinessDivisionType,
      }),
      getShopContext({
        id: shop?.id,
        shop_id: shop?.id,
        license_id: shop?.lipaId,
      }),
      getMapABTestContext(isMapToggledOnInitially),
      getAiSearchContext(
        mapSearchStateToAiSearchContext({
          preferences: initialFilters.preferences,
          isAiSearchEnabled: additionalUIStateParams?.searchMode === SearchMode.AI,
        }),
      ),
    ],
    [
      isLipaPortfolioPage,
      language,
      countryCode,
      initialFilters,
      currency,
      measurementSystem,
      isMapToggledOnInitially,
      additionalUIStateParams?.sortingOptionId,
      additionalUIStateParams?.searchMode,
      shop?.id,
      shop?.lipaId,
    ],
  );

  useGTMTracking();
  useTrackPageView(pageViewContexts);
  useGTMTackingIdsEvents({ trackingIds });
  useGAEventOnce('searchResult');
  useGAEvent('searchResultLicensePartner', hasLicencePartnerGAAccount);
  useGTMDataLayer('type', 'SEARCH_RESULTS_NEW');

  return (
    <>
      <Head
        title={tCommon('search.head.index.title')}
        description={tCommon(
          'search.head.index.description',
          metaDescriptionsIndex(tSearchModule, initialFilters, additionalUIStateParams?.placeName),
        )}
        isIndexed={false}
      />
      <Layout
        spearHeadNavigation={spearHeadNavigation}
        pageLanguages={pageLanguages}
        onContactCtaClick={open}
        filters={initialFilters}
        isPropertySearchPage
        countryLinks={countryLinks}
        isMapToggledOn={isMapToggledOnInitially}
        searchMode={additionalUIStateParams?.searchMode}
        shop={shop}
      >
        <main className={styles.container} data-testid="property-search-page-container">
          <Headline className="sr-only" label={tCommon('search.head.index.title')} as="h1" />
          <SearchModule
            countryCode={countryCode}
            initialFilters={initialFilters}
            shouldApplySessionBounds={additionalUIStateParams?.hasSessionBounds}
            apiConfig={searchApi.config}
            translationResolver={tSearchModule as TFunction}
            imageComponent={ImageComponent}
            linkComponent={SearchResultLink}
            transComponent={SearchModuleTransComponent}
            watchlist={watchlist}
            userAccountProps={{
              translationResolver: tUserAccount as TFunction,
            }}
            events={{
              onWatchlistClick: (property) => handleWatchlistClick(property),
              onError: datadogRum.addError,
            }}
            tracking={{
              snowplowApplicationId: 'new-search',
              isSnowplowInitialized,
              pageType: isLipaPortfolioPage ? PageType.LIPA_SEARCH_RESULT : PageType.SEARCH_RESULT,
              templateType: TemplateType.SEARCH_RESULT,
            }}
            featureFlags={{
              [FeatureFlags.IS_AI_SEARCH_ENABLED]: featureFlags?.isAiSearchEnabled,
              [FeatureFlags.IS_MAP_ENABLED]: true,
              [FeatureFlags.IS_SMART_SORT_ENABLED]: featureFlags?.isSmartSortEnabled,
            }}
            mapBoundingBoxFallback={mapBoundingBoxFallback}
            isFilterBarSticky
            willReflectInitialFiltersInUrl
            config={CONFIG}
            isMapToggledOnInitially={isMapToggledOnInitially}
            options={{
              ...initialOptions,
              page: initialOptions.page ?? INTIAL_OPTIONS.page,
              pageSize: initialOptions.pageSize ?? INTIAL_OPTIONS.pageSize,
              currency,
              measurementSystem,
              language,
              marketCountryCode: countryCode,
            }}
            willSyncStateWithUrl
            queryClient={queryClient}
            initialPlaceName={additionalUIStateParams?.placeName}
            initialSearchMode={additionalUIStateParams?.searchMode}
            initialSortingOptionId={additionalUIStateParams?.sortingOptionId ?? undefined}
            smartSortPreferences={smartSortPreferences}
          />
        </main>
      </Layout>
    </>
  );
};

const PropertySearchPage = (pageProps: PropertySearchPageProps): ReactElement => {
  return (
    <SearchPageProviders {...pageProps}>
      <SearchPageContent />
    </SearchPageProviders>
  );
};

export { propertySearchPageGetServerSideProps as getServerSideProps } from '@/services/SSRFunctions/PropertySearch/getServerSideProps';
export default PropertySearchPage;
